import type { UnifiedChatContentRecommendation } from '@year13/kui/types/contentRecommendation';
import type { MaybeRefOrGetter } from 'vue';

import { sponsoringPartner } from '~kui/components/KUI/Unified/Asset/assetCardSponsorship';

import type { CollectionCategory, LearningAssetCollectionChildFieldsFragment } from '~/graphql/generated/graphql';

import { useLoggedFailure } from '~/composables/unified/useLoggedFailure';
import { useRewardsSuppressed } from '~/composables/unified/useRewardsSuppressed';
import { coinValueFragment } from '~/graphql/documents/content';
import { publicFileFragment } from '~/graphql/documents/core';
import { fetchCollectionsByCategory, learningAssetCollectionChildFieldsFragment } from '~/graphql/documents/learn';
import { useFragment } from '~/graphql/generated/fragment-masking';
import { coverFallbackImage } from '~/utils/coverFallbackImage';

export type CollectionRec = Extract<UnifiedChatContentRecommendation, { type: 'learning_asset_collection' }>;
type LearningAssets = LearningAssetCollectionChildFieldsFragment['learningAssets'];

const computeDuration = (assets: LearningAssets): number | null => {
  const total = assets.reduce((sum, a) => sum + (a.duration ?? 0), 0);

  return total || null;
};

const computeCoins = (assets: LearningAssets): number | null => {
  const total = assets.reduce((sum, a) => sum + (useFragment(coinValueFragment, a.coinValue)?.coins ?? 0), 0);

  return total || null;
};

interface MapOptions {
  rewardsSuppressed: boolean;
  includeViewerProgress: boolean;
}

// The card a collection makes, by the catalogue card's rules (`transformCollectionCatalogueItem`):
// totals and progress over published assets only, the fallback cover, no coins where rewards are
// suppressed, and the named sponsor picked the same way.
const mapCollectionToRec = (c: LearningAssetCollectionChildFieldsFragment, { rewardsSuppressed, includeViewerProgress }: MapOptions): CollectionRec => {
  const coverImage = useFragment(publicFileFragment, c.coverImage);
  const partner = sponsoringPartner(c.partners);
  const partnerLogo = partner ? useFragment(publicFileFragment, partner.iconFile) : null;
  const published = c.learningAssets.filter(a => a.publishedAt);

  return {
    type: 'learning_asset_collection',
    id: c.id,
    name: c.name,
    slug: c.slug,
    imageUrl: coverImage?.publicUrl || coverFallbackImage(c.id),
    collectionType: 'CHANNEL',
    completedCount: includeViewerProgress ? published.filter(a => a.currentUserProgress?.completedAt).length : 0,
    totalCount: includeViewerProgress ? published.length : 0,
    partnerLogoUrl: partnerLogo?.publicUrl ?? undefined,
    partnerName: partner?.name ?? undefined,
    duration: computeDuration(published),
    coins: rewardsSuppressed ? null : computeCoins(published),
  };
};

interface UseCategoryCollectionRecsOptions {
  category: CollectionCategory;
  server?: boolean;
  // Skips the request while false (a surface that hides the cards), keeping what it last had,
  // server-rendered or not, so the cards are ready the moment they show again.
  enabled?: MaybeRefOrGetter<boolean>;
  // Surface the viewer's progress (the completed tick). Off, a card reports none, as the cards on
  // /learn and / always have.
  includeViewerProgress?: boolean;
}

/**
 * The collections an admin has placed in a category (/content/categories), in the order set
 * there, as cards.
 */
export const useCategoryCollectionRecs = ({ category, server = false, enabled = true, includeViewerProgress = false }: UseCategoryCollectionRecsOptions) => {
  const { $urqlClient } = useNuxtApp();
  const rewardsSuppressed = useRewardsSuppressed();

  const fetchCategory = async () => {
    const result = await $urqlClient.query(
      fetchCollectionsByCategory,
      { category },
      { requestPolicy: 'cache-and-network' },
    ).toPromise();

    if (result.error) {
      throw result.error;
    }

    return result.data ?? null;
  };

  // `enabled` false answers with the data already held; the watch refetches once it is true again.
  const { data, pending, error } = useAsyncData(
    `category-collections-${category}`,
    fetchCategory,
    { server, enabled: () => toValue(enabled), watch: [() => toValue(enabled)] },
  );

  const recs = computed<CollectionRec[]>(() => {
    const entries = data.value?.collectionsInCategory ?? [];

    return entries.map(entry => mapCollectionToRec(useFragment(learningAssetCollectionChildFieldsFragment, entry), { rewardsSuppressed: rewardsSuppressed.value, includeViewerProgress }));
  });

  useLoggedFailure({ error, message: `Failed to load ${category} collections` });

  return { recs, pending, error };
};
